import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { isProd } from '../../../../config';
import { FilterItemModel, SubnodesItem } from '../../../../models/filter.model';
import { bookingYear, DataSchemeFilter, filterData, getProductList, updateDataFilter } from '../../../../store/location';
import { abortSearch } from '../../../../store/location/actions';
import { loadersByName, togglePopup } from '../../../../store/ui';
import { getLabelFilterBlock, resetFilterData } from '../../../../utils';
import Container from '../base/container';
import Button, { ButtonKind } from '../button/Button';
import { Checkbox } from '../input';
import './index.scss';

const Filters = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const filterDataState = useSelector(filterData);
    const bookingyear = useSelector(bookingYear);
    const [filters, setFilters] = useState<DataSchemeFilter>(filterDataState);
    const filterLoading = useSelector(loadersByName('filter'));
    const collapsableFilters = ['airport'];
    const [collapsedFilters, setCollapsedFilters] = useState<string[]>(collapsableFilters);

    useEffect(() => window.scrollTo(0, 0), []);

    useEffect(() => setFilters(filterDataState), [filterDataState]);

    const handleChangeInput = (key: keyof DataSchemeFilter, index: number) => (item: FilterItemModel) => {
        const newDataByKey = [...(filters[key] as FilterItemModel[])];
        newDataByKey[index] = { ...item, checked: !item.checked };
        setFilters({ ...filters, [key]: newDataByKey });
    };

    const handleChangeChild = (key: keyof DataSchemeFilter, index: number) => (item: FilterItemModel, indexItem: number) => {
        const newDataByKey = [...(filters[key] as FilterItemModel[])];
        const children = [...item.subnodes];
        const el = children[indexItem];
        const checked = el.checked !== undefined ? !el.checked : true;
        newDataByKey[index].subnodes[indexItem] = { ...el, checked: checked };
        setFilters({ ...filters, [key]: newDataByKey });
    };

    const handleThirdLevelChange = (
        key: keyof DataSchemeFilter,
        index: number,
        item: FilterItemModel,
        indexItem: number,
        indexSubitem: number
    ) => {
        const newDataByKey = [...(filters[key] as FilterItemModel[])];
        const children = [...item.subnodes];
        const el = children[indexItem];

        if (el.subnodes || !isEmpty(el.subnodes)) {
            const subchildren = [...(el.subnodes || [])];
            const subel = subchildren[indexSubitem];
            const checked = subel.checked !== undefined ? !subel.checked : true;
            // @ts-ignore
            newDataByKey[index].subnodes[indexItem].subnodes[indexSubitem] = { ...subel, checked: checked };
            setFilters({ ...filters, [key]: newDataByKey });
        }
    };

    const getThirdLevel = (
        el: SubnodesItem,
        key: keyof DataSchemeFilter,
        idx: number,
        item: FilterItemModel,
        count: number,
        indexItem: number
    ) => {
        if (!el.subnodes || isEmpty(el.subnodes)) return null;

        return (
            <>
                {el.subnodes.map((subel: SubnodesItem, j: number) => {
                    return (
                        <Checkbox
                            key={`${key}_${String(subel.label)}_${String(subel.code)}`}
                            id={`checkbox_${String(subel.label)}_${String(subel.code)}`}
                            onChange={() => handleThirdLevelChange(key, idx, item, indexItem, j)}
                            checked={subel.checked ? subel.checked : false}
                            label={`${String(subel.label)} ${!isProd && count ? `(${count})` : ''}`}
                            className={count == 0 ? 'checkbox-disabled' : ''}
                            disabled={filterLoading}
                        />
                    );
                })}
            </>
        );
    };

    const collapseExpand = (code: string) => {
        const isCollapsed = collapsedFilters.includes(code);
        if (isCollapsed) {
            setCollapsedFilters(collapsedFilters.filter(item => item !== code));
        } else {
            setCollapsedFilters([...collapsedFilters, code]);
        }
    };

    const getSubnodes = (
        key: keyof DataSchemeFilter,
        idx: number,
        item: FilterItemModel,
        count: number,
        code: string | number,
        subnodes: SubnodesItem[],
        label: string,
        input: JSX.Element,
        isCollapsable: boolean
    ) => {
        const isCollapsed = collapsedFilters.includes(code as string);
        const isShow = isCollapsable ? !isCollapsed : true;

        return (
            <div className={'form-checkbox-groups'} key={`div_${key}_${code}_${idx}`}>
                {isCollapsable && (
                    <div className="collapse-toggle" onClick={() => collapseExpand(code as string)}>
                        {isCollapsed ? '+' : '-'}
                    </div>
                )}
                {code ? input : <p style={{ margin: '20px 0 10px' }}>{label}</p>}

                {isShow && (
                    <div style={{ margin: `0 0 20px 30px` }}>
                        {subnodes.map((el: SubnodesItem, i: number) => {
                            const { code, label, checked } = el;

                            if (el.subnodes && !isEmpty(el.subnodes)) {
                                return (
                                    <div>
                                        <p style={{ margin: '20px 0 10px' }}>{label}</p>
                                        <div
                                            style={{
                                                margin: `0 50px 20px 30px`,
                                                paddingRight: '20px',
                                            }}
                                            className="filters-level-3"
                                        >
                                            {getThirdLevel(el, key, idx, item, count || 0, i)}
                                        </div>
                                    </div>
                                );
                            } else {
                                return (
                                    <Checkbox
                                        key={`${key}_${String(label)}_${String(code)}`}
                                        id={`checkbox_${String(label)}_${String(code)}`}
                                        onChange={() => handleChangeChild(key, idx)(item, i)}
                                        checked={checked ? checked : false}
                                        label={`${String(label)} ${!isProd && count ? `(${count})` : ''}`}
                                        className={count == 0 ? 'checkbox-disabled' : ''}
                                        disabled={filterLoading}
                                    />
                                );
                            }
                        })}
                    </div>
                )}
            </div>
        );
    };

    const getFiltersContent = () => {
        const dataKey = Object.keys(filters).filter(item => item !== 'bookingyear') as Array<keyof DataSchemeFilter>;
        return (
            <div className="filter-modal">
                <div className="filter-block">
                    {!isEmpty(dataKey) &&
                        dataKey.map((key: keyof DataSchemeFilter, i: number) => {
                            return (
                                <div
                                    key={`${key}_${i}`}
                                    className={`${key === 'format' ? 'format-block' : ''} filter-block__item`}
                                >
                                    <span className="filter-block__item-title">{getLabelFilterBlock(intl)(key)}</span>

                                    {/* <Divider margin={'18px 0 23px'} width={'95%'} /> */}

                                    {!isEmpty(filters[key]) &&
                                        typeof filters[key] !== 'string' &&
                                        typeof filters[key] !== 'number' &&
                                        (filters[key] as FilterItemModel[]).map((item: FilterItemModel, idx) => {
                                            const { label, count, code, checked, subnodes, ...props } = item;

                                            const input = (
                                                <Checkbox
                                                    key={`${key}_${code}`}
                                                    id={`checkbox_${key}_${code}`}
                                                    onChange={() => handleChangeInput(key, idx)(item)}
                                                    checked={checked}
                                                    label={`${label} ${!isProd && count ? `(${count})` : ''}`}
                                                    className={count == 0 ? 'checkbox-disabled' : ''}
                                                    disabled={filterLoading}
                                                    {...props}
                                                />
                                            );

                                            if (subnodes && !isEmpty(subnodes)) {
                                                return getSubnodes(
                                                    key,
                                                    idx,
                                                    item,
                                                    count || 0,
                                                    code,
                                                    subnodes,
                                                    label,
                                                    input,
                                                    collapsableFilters.includes(code as string)
                                                );
                                            }

                                            return input;
                                        })}
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    };

    if (!isEmpty(filters)) {
        return (
            <div className="filter-modal">
                <div className="filter-block">{getFiltersContent()}</div>

                <div className={'filter-modal__footer'}>
                    <Container>
                        <Button kind={ButtonKind.LINK} onClick={() => setFilters(resetFilterData(filters))}>
                            {intl.formatMessage({ id: 'location.filter.clearBtn', defaultMessage: 'Löschen' })}
                        </Button>

                        <Button
                            onClick={() => {
                                dispatch(abortSearch());
                                dispatch(updateDataFilter(filters));
                                dispatch(togglePopup({ name: 'location_filter', status: false }));
                                dispatch(getProductList({ reset: true, bookingyear: bookingyear }));
                            }}
                        >
                            {intl.formatMessage({ id: 'location.filter.updateBtn', defaultMessage: 'Aktualisieren' })}
                        </Button>
                    </Container>
                </div>
            </div>
        );
    }

    return null;
};

export default Filters;
